import * as mui from '@mui/material'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import { langSwitchContext, translationsContext } from 'context/translations'
import { Link } from 'components/Link'
import { routes } from 'routes'
import { graphqlClient, requireErrorExtensions , useQuery} from 'utils/graphql'

import logoIcon from '../assets/logo.png'
import { LanguageSelector } from '../LanguageSelector'


export function ResetPassword() {
  const t = React.useContext(translationsContext)
  const [language, setLanguage] = React.useContext(langSwitchContext)

  const validationSchema = Yup.object().shape({
    password: Yup.string().required(t('validation__required')).min(8, t('validation__short_password')),
    password2: Yup.string().required(t('validation__required')).oneOf([Yup.ref('password')], t('validation__password_mismatch')),
  })
  type valuesType = Yup.InferType<typeof validationSchema>

  const [commonErrors, setCommonErrors] = React.useState<string[]>([])
  const [fieldErrors, setFieldErrors] = React.useState<{[k: string]: string}>({})

  const { token, lang } = useParams()

  React.useEffect(() => {
    if (lang) setLanguage(lang as any)
  }, [lang])

  const navigate = useNavigate()

  const tokenIsValid = useQuery(
    {resetPasswordTokenValid: [{token}]},
    'resetPasswordTokenValid'
  )

  function handleSubmit(
    values: valuesType,
    {setSubmitting}: {setSubmitting: (isSubmitting: boolean) => void}
  ) {
    setCommonErrors([])
    graphqlClient.chain.mutation
      .resetPassword({language: language, token: token, ...values})
      .execute({__scalar: 1})
      .then(() => navigate(routes.LOGIN+'?reset=true'))
      .catch(e => {
        const extensions = requireErrorExtensions(e)
        setCommonErrors(extensions.common_errors || [])
        setFieldErrors(extensions.field_errors || {})
      })
      .finally(() => setSubmitting(false))
  }

  if (tokenIsValid.spinner) return tokenIsValid.spinner

  return (
    <mui.Container component="main" maxWidth="xs">
      <mui.Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <img src={logoIcon} alt="logo" height="100"/>
        <mui.Typography component="h1" variant="h5" mt={4}>
          {t('forgot_password__title')}
        </mui.Typography>
        <Formik
          initialValues={{password: '', password2: ''}}
          onSubmit={handleSubmit}
          sx={{mt: 1}}
          validateOnBlur={true}
          validateOnChange={false}
          validationSchema={validationSchema}
        >
          {({submitForm, isSubmitting, touched, errors, isValid}) => (
            <Form style={{width: '100%'}}>
              <mui.Stack spacing={3} mt={3}>
                {!tokenIsValid.data
                  ? <mui.Alert severity="error">{t('api__mutation__reset_password__err__expired')}</mui.Alert>
                  : <>
                    {commonErrors.map((error, i) => (
                      <mui.Alert key={i} severity="error">
                        {t(`api__${error}`)}
                      </mui.Alert>
                    ))}
                    <Field
                      component={TextField}
                      error={touched.password && (!!errors.password || !!fieldErrors.password)}
                      helperText={touched.password && (errors.password || fieldErrors.password)}
                      InputProps={{autoComplete: 'off', fullWidth: true, type: 'password'}}
                      label={t('registration__field__password_1')}
                      name="password"
                    />
                    <Field
                      component={TextField}
                      error={touched.password2 && (!!errors.password2 || !!fieldErrors.password2)}
                      helperText={touched.password2 && (errors.password2 || fieldErrors.password2)}
                      InputProps={{autoComplete: 'off', fullWidth: true, type: 'password'}}
                      label={t('registration__field__password_2')}
                      name="password2"
                    />
                    <mui.Button
                      disabled={
                        isSubmitting ||
                        !isValid ||
                        Object.keys(fieldErrors).length > 0 ||
                        Object.keys(touched).length === 0
                      }
                      fullWidth
                      onClick={submitForm}
                      sx={{
                        mt: 3,
                        mb: 2,
                        py: 1.5,
                        backgroundColor: '#A8C02A',
                        '&:hover': {
                          backgroundColor: '#C1CC00',
                        },
                      }}
                      variant="contained"
                    >
                      Reset password
                    </mui.Button>
                  </>
                }
                <mui.Stack direction="row" justifyContent="space-between">
                  <mui.Stack>
                    {!tokenIsValid.data && (
                      <Link to={routes.FORGOT_PASSWORD} variant="body2">
                        {t('forgot_password__link__request_new')}
                      </Link>
                    )}
                    <Link to={routes.LOGIN} variant="body2">
                      {t('forgot_password__link__back_to_login')}
                    </Link>
                  </mui.Stack>
                  <LanguageSelector />
                </mui.Stack>
              </mui.Stack>
            </Form>
          )}
        </Formik>
      </mui.Box>
    </mui.Container>
  )
}